import { SaveAlt } from "@material-ui/icons";
import { Card, Fab, ThemeProvider, Tooltip } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import LoadingScreen from "components/loading/LoadingScreen";
import Table from "components/table/Table";
import { ALL_OPTION } from "constant";
import { useFormik } from "formik";
import { convertBusinessCodeToBusinessName } from "helper/business-management-helper";
import { downloadFileByS3Link } from "helper/common-helper";
import { usePagination } from "hooks/usePagination";
import moment from "moment";
import { useState } from "react";
import { useMemo } from "react";
import { useGetListBusiness } from "service/query/business-management.query";
import { useGetCallLogReports } from "service/query/report.query";
import muiTheme from "theme/muiTheme";
import * as Yup from "yup";

import BusinessReportsFilter from "./components/BusinessReportsFilter";

const BusinessReports = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const DEFAULT_FORM_VALUE = {
    pbxType: ALL_OPTION.value,
    businessCode: "",
    type: "",
    dateRange: [yesterday, yesterday],
    month: new Date(),
    scheduleType: "daily",
  };

  const [filter, setFilter] = useState();

  const { page, pageSize, handleChangePage, handleChangePageSize } =
    usePagination();

  const { data: listBusiness } = useGetListBusiness();

  const handleSubmit = (values) => {
    setFilter(convertFormValueToFilter(values));
  };

  const formik = useFormik({
    initialValues: DEFAULT_FORM_VALUE,
    validationSchema: Yup.object().shape({
      businessCode: Yup.string().required("Không được trống"),
      type: Yup.string().required("Không được trống"),
    }),
    onSubmit: handleSubmit,
  });

  const { data: listBusinessReport, isFetching } = useGetCallLogReports(
    {
      page,
      size: pageSize,
      ...filter,
    },
    {
      refetchInterval: false,
      placeholderData: keepPreviousData,
      enabled: !!filter,
    }
  );

  const columns = useMemo(
    () => [
      {
        id: "name",
        name: "Tên báo cáo",
      },
      {
        id: "businessName",
        name: "Nghiệp vụ",
        render: (record) =>
          convertBusinessCodeToBusinessName(listBusiness, record.businessCode),
      },
      {
        id: "date",
        name: "Ngày",
        render: (record) =>
          `${
            record.startDate
              ? moment(record.startDate).format("DD/MM/YYYY")
              : ""
          } - ${
            record.endDate ? moment(record.endDate).format("DD/MM/YYYY") : ""
          }`,
      },
      {
        id: "download",
        name: "Tải xuống",
        render: (record) =>
          record.S3Url && (
            <Tooltip title="Tải xuống">
              <Fab
                color="primary"
                size="small"
                aria-label="export"
                onClick={() => downloadFileByS3Link(record.S3Url)}>
                <SaveAlt />
              </Fab>
            </Tooltip>
          ),
      },
    ],
    [listBusiness]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Card sx={{ p: 2 }}>
        <BusinessReportsFilter formik={formik} />
      </Card>

      <Card sx={{ mt: 2 }}>
        <Table
          columns={columns}
          data={listBusinessReport?.docs}
          toolbar={{
            title: "Danh sách báo cáo",
          }}
          pagination={{
            page,
            pageSize,
            totalItems: listBusinessReport?.total,
            onPageChange: handleChangePage,
            onPageSizeChange: handleChangePageSize,
          }}
        />
      </Card>

      <LoadingScreen loading={isFetching} />
    </ThemeProvider>
  );
};

const convertFormValueToFilter = ({
  businessCode,
  type,
  dateRange,
  scheduleType,
  month,
}) => {
  return {
    businessCode: businessCode === ALL_OPTION.value ? null : businessCode,
    type: type === ALL_OPTION.value ? null : type,
    scheduleType,
    fromDate:
      scheduleType === "daily"
        ? moment(dateRange[0]).startOf("date").toISOString()
        : moment(month).startOf("month").toISOString(),
    toDate:
      scheduleType === "daily"
        ? moment(dateRange[1]).endOf("date").toISOString()
        : moment(month).endOf("month").toISOString(),
  };
};

export default BusinessReports;
